/**
 * Check-in form component - Two-step check-in flow
 */

import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { databaseService } from '../services/database.js';
import type { CreateCheckInData, MediaItem } from '../models/check-in.js';
import { calculateElapsedPeriods, formatDuration, formatPeriodRange, getNextFullPeriod, getCurrentPeriod } from '../utils/time.js';
import { validateCheckInData } from '../utils/validation.js';
import './media-recorder.js';

@customElement('check-in-form')
export class CheckInForm extends LitElement {
  static styles = css`
    :host {
      display: flex;
      flex-direction: column;
      flex: 1;
      min-height: 0;
    }

    .container {
      max-width: 600px;
      margin: 0 auto;
      width: 100%;
      display: flex;
      flex-direction: column;
      flex: 1;
      min-height: 0;
    }

    .card {
      background-color: var(--bg-secondary);
      border-radius: 0;
      padding: 0.5rem;
      border: 1px solid var(--color-border);
      display: flex;
      flex-direction: column;
      flex: 1;
      min-height: 0;
    }

    .step-content {
      display: flex;
      flex-direction: column;
      flex: 1;
      min-height: 0;
    }

    .step-indicator {
      display: flex;
      gap: 0.25rem;
      margin-bottom: 0.5rem;
      justify-content: flex-start;
    }

    .step {
      width: 20px;
      height: 2px;
      background-color: var(--bg-tertiary);
      border-radius: 0;
      transition: none;
    }

    .step.active {
      background-color: var(--color-primary);
    }

    .title {
      font-size: 0.9rem;
      font-weight: 400;
      color: var(--text-primary);
      margin-bottom: 0.25rem;
    }

    .subtitle {
      font-size: 0.75rem;
      color: var(--text-secondary);
      margin-bottom: 0.5rem;
    }

    .period-info {
      padding: 0.25rem 0.5rem;
      background-color: var(--bg-tertiary);
      border-radius: 0;
      margin-bottom: 0.5rem;
      font-size: 0.75rem;
      color: var(--text-secondary);
      border-left: 2px solid var(--color-primary);
    }

    .period-info strong {
      color: var(--text-primary);
      display: inline;
      margin-right: 0.5rem;
    }

    .media-section {
      margin-bottom: 0.5rem;
    }

    .media-controls {
      display: flex;
      align-items: center;
      gap: 0.25rem;
    }

    .media-previews {
      display: flex;
      flex-wrap: wrap;
      gap: 0.25rem;
      margin-top: 0.25rem;
    }

    .media-preview {
      display: flex;
      align-items: center;
      gap: 0.25rem;
      padding: 0.25rem;
      background-color: var(--bg-tertiary);
    }

    .media-preview img {
      width: 40px;
      height: 40px;
      object-fit: cover;
    }

    .media-preview audio {
      height: 24px;
    }

    .remove-button {
      padding: 0.1rem 0.25rem;
      background-color: transparent;
      color: var(--text-tertiary);
      border: none;
      font-size: 0.7rem;
      cursor: pointer;
      min-height: 44px;
      min-width: 28px;
    }

    .remove-button:hover {
      color: var(--color-error);
    }

    .form-group {
      display: flex;
      flex-direction: column;
      flex: 1;
      min-height: 0;
    }

    .label {
      display: block;
      font-weight: 400;
      color: var(--text-secondary);
      margin-bottom: 0.25rem;
      font-size: 0.75rem;
    }

    .textarea {
      width: 100%;
      flex: 1;
      min-height: 80px;
      padding: 0.25rem 0.5rem;
      background-color: var(--bg-primary);
      color: var(--text-primary);
      border: 1px solid var(--color-border);
      border-radius: 0;
      resize: none;
      font-family: inherit;
      font-size: 0.85rem;
      transition: none;
      box-sizing: border-box;
    }

    .textarea:focus {
      border-color: var(--color-primary);
      outline: none;
    }

    .actions {
      display: flex;
      gap: 0.5rem;
      margin-top: 0.5rem;
    }

    .button {
      flex: 1;
      padding: 0.25rem 0.5rem;
      border-radius: 0;
      font-weight: 400;
      font-size: 0.85rem;
      cursor: pointer;
      transition: none;
    }

    .button-primary {
      background-color: var(--color-primary);
      color: white;
      border: none;
    }

    .button-primary:hover:not(:disabled) {
      background-color: var(--color-primary-hover);
    }

    .button-primary:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .button-secondary {
      background-color: transparent;
      color: var(--text-primary);
      border: 1px solid var(--color-border);
    }

    .button-secondary:hover {
      background-color: var(--bg-tertiary);
    }

    .error {
      color: var(--color-error);
      font-size: 0.75rem;
      margin-top: 0.25rem;
    }
  `;

  @state() private step: 1 | 2 = 1;
  @state() private pastDescription = '';
  @state() private futureDescription = '';
  @state() private pastMedia: MediaItem[] = [];
  @state() private futureMedia: MediaItem[] = [];
  @state() private errors: string[] = [];
  @state() private submitting = false;
  @state() private lastCheckInTime: number | null = null;
  @state() private elapsedPeriods = 0;
  @state() private planCurrentPeriod = false;

  private blobUrls = new Map<Blob, string>();

  private getBlobUrl(blob: Blob): string {
    let url = this.blobUrls.get(blob);
    if (!url) {
      url = URL.createObjectURL(blob);
      this.blobUrls.set(blob, url);
    }
    return url;
  }

  async connectedCallback() {
    super.connectedCallback();
    await this.loadLastCheckIn();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    for (const url of this.blobUrls.values()) {
      URL.revokeObjectURL(url);
    }
    this.blobUrls.clear();
  }

  updated() {
    const textarea = this.renderRoot.querySelector('.textarea') as HTMLTextAreaElement | null;
    const root = this.renderRoot as ShadowRoot;
    if (textarea && root.activeElement !== textarea) {
      textarea.focus();
    }
  }

  private async loadLastCheckIn() {
    const lastCheckIn = await databaseService.getLastCheckIn();
    console.log('loadLastCheckIn - lastCheckIn:', lastCheckIn);
    if (lastCheckIn) {
      this.lastCheckInTime = lastCheckIn.checkInTime;
      console.log('loadLastCheckIn - lastCheckInTime:', new Date(this.lastCheckInTime).toLocaleString());
      console.log('loadLastCheckIn - pastDescription:', lastCheckIn.pastDescription);
      this.elapsedPeriods = calculateElapsedPeriods(lastCheckIn.checkInTime);

      // Check if the last check-in planned the current period
      const currentPeriod = getCurrentPeriod();
      const currentPeriodStart = currentPeriod.start.getTime();

      // If the planned period matches current period, we already have a plan
      this.planCurrentPeriod = lastCheckIn.nextPlannedPeriod.start !== currentPeriodStart;
    } else {
      // First check-in, plan current period
      this.planCurrentPeriod = true;
    }
  }

  private handleNext() {
    if (!this.pastDescription.trim()) {
      this.errors = ['Please describe what you did'];
      return;
    }

    this.errors = [];
    this.step = 2;
  }

  private handleBack() {
    this.step = 1;
    this.errors = [];
  }

  private async handleSubmit() {
    const validation = validateCheckInData({
      pastDescription: this.pastDescription,
      futureDescription: this.futureDescription
    });

    if (!validation.valid) {
      this.errors = validation.errors;
      return;
    }

    this.errors = [];
    this.submitting = true;

    try {
      const checkInData: CreateCheckInData = {
        pastDescription: this.pastDescription,
        futureDescription: this.futureDescription,
        pastMedia: this.pastMedia.length > 0 ? this.pastMedia : undefined,
        futureMedia: this.futureMedia.length > 0 ? this.futureMedia : undefined
      };

      await databaseService.createCheckIn(checkInData, this.lastCheckInTime, this.planCurrentPeriod);

      // Update app state with new check-in time
      await databaseService.updateAppState({
        lastCheckInTime: Date.now()
      });

      // Emit completion event
      this.dispatchEvent(new CustomEvent('check-in-complete', {
        bubbles: true,
        composed: true
      }));

      // Reset form
      this.resetForm();
    } catch (error) {
      console.error('Failed to submit check-in:', error);
      this.errors = ['Failed to save check-in. Please try again.'];
      this.submitting = false;
    }
  }

  private async resetForm() {
    this.step = 1;
    this.pastDescription = '';
    this.futureDescription = '';
    this.pastMedia = [];
    this.futureMedia = [];
    this.errors = [];
    this.submitting = false;

    // Clean up blob URLs
    for (const url of this.blobUrls.values()) {
      URL.revokeObjectURL(url);
    }
    this.blobUrls.clear();

    // Reload last check-in time to get fresh elapsed time
    await this.loadLastCheckIn();
  }

  private handleMediaCaptured(step: 'past' | 'future', event: CustomEvent) {
    const { blob, type, contentType } = event.detail;
    const item: MediaItem = { blob, mediaType: type, contentType };

    if (step === 'past') {
      this.pastMedia = [...this.pastMedia, item];
    } else {
      this.futureMedia = [...this.futureMedia, item];
    }
  }

  private handleRemoveMedia(step: 'past' | 'future', index: number) {
    if (step === 'past') {
      const item = this.pastMedia[index];
      const url = this.blobUrls.get(item.blob);
      if (url) {
        URL.revokeObjectURL(url);
        this.blobUrls.delete(item.blob);
      }
      this.pastMedia = this.pastMedia.filter((_, i) => i !== index);
    } else {
      const item = this.futureMedia[index];
      const url = this.blobUrls.get(item.blob);
      if (url) {
        URL.revokeObjectURL(url);
        this.blobUrls.delete(item.blob);
      }
      this.futureMedia = this.futureMedia.filter((_, i) => i !== index);
    }
  }

  private renderMediaPreviews(step: 'past' | 'future') {
    const items = step === 'past' ? this.pastMedia : this.futureMedia;
    if (items.length === 0) return '';

    return html`
      <div class="media-previews">
        ${items.map((item, i) => html`
          <div class="media-preview">
            ${item.mediaType === 'photo'
              ? html`<img src="${this.getBlobUrl(item.blob)}" alt="Captured photo" />`
              : html`<audio src="${this.getBlobUrl(item.blob)}" controls></audio>`
            }
            <button class="remove-button" type="button" @click=${() => this.handleRemoveMedia(step, i)}>
              ✕
            </button>
          </div>
        `)}
      </div>
    `;
  }

  render() {
    return html`
      <div class="container">
        <div class="card">
          <div class="step-indicator">
            <div class="step ${this.step === 1 ? 'active' : ''}"></div>
            <div class="step ${this.step === 2 ? 'active' : ''}"></div>
          </div>

          <div class="step-content">
            ${this.step === 1 ? this.renderStepOne() : this.renderStepTwo()}
          </div>

          ${this.errors.length > 0 ? html`
            <div class="error">
              ${this.errors.map(err => html`<div>${err}</div>`)}
            </div>
          ` : ''}
        </div>
      </div>
    `;
  }

  private renderStepOne() {
    const elapsedTime = this.lastCheckInTime
      ? formatDuration(Date.now() - this.lastCheckInTime)
      : 'first check-in';

    return html`
      <div class="title">reflection</div>
      <div class="subtitle">what happened since last check-in</div>

      <div class="period-info">
        <strong>elapsed:</strong>
        ${elapsedTime}
        ${this.elapsedPeriods > 0 ? ` (${this.elapsedPeriods} period${this.elapsedPeriods > 1 ? 's' : ''})` : ''}
      </div>

      <div class="media-section">
        <div class="media-controls">
          <media-recorder
            type="photo"
            @media-captured=${(e: CustomEvent) => this.handleMediaCaptured('past', e)}
          ></media-recorder>
          <media-recorder
            type="audio"
            @media-captured=${(e: CustomEvent) => this.handleMediaCaptured('past', e)}
          ></media-recorder>
        </div>
        ${this.renderMediaPreviews('past')}
      </div>

      <div class="form-group">
        <label class="label" for="past-description">
          description
        </label>
        <textarea
          id="past-description"
          class="textarea"
          autofocus
          .value=${this.pastDescription}
          @input=${(e: Event) => {
            this.pastDescription = (e.target as HTMLTextAreaElement).value;
          }}
          placeholder="what did i work on? what did i learn?"
        ></textarea>
      </div>

      <div class="actions">
        <button class="button button-primary" @click=${this.handleNext}>
          next →
        </button>
      </div>
    `;
  }

  private renderStepTwo() {
    const currentPeriod = getCurrentPeriod();

    // Always plan activity periods only
    let period: any;
    let periodLabel: string;

    if (this.planCurrentPeriod && currentPeriod.type === 'activity') {
      // Planning current activity period
      period = currentPeriod;
      periodLabel = 'current period';
    } else {
      // Planning next activity period (skips rest periods)
      period = getNextFullPeriod();
      periodLabel = 'next period';
    }

    return html`
      <div class="title">plan</div>
      <div class="subtitle">set intention for next 25min</div>

      <div class="period-info">
        <strong>${periodLabel}:</strong>
        ${formatPeriodRange(period)}
      </div>

      <div class="media-section">
        <div class="media-controls">
          <media-recorder
            type="photo"
            @media-captured=${(e: CustomEvent) => this.handleMediaCaptured('future', e)}
          ></media-recorder>
          <media-recorder
            type="audio"
            @media-captured=${(e: CustomEvent) => this.handleMediaCaptured('future', e)}
          ></media-recorder>
        </div>
        ${this.renderMediaPreviews('future')}
      </div>

      <div class="form-group">
        <label class="label" for="future-description">
          intention
        </label>
        <textarea
          id="future-description"
          class="textarea"
          autofocus
          .value=${this.futureDescription}
          @input=${(e: Event) => {
            this.futureDescription = (e.target as HTMLTextAreaElement).value;
          }}
          placeholder="what will i focus on?"
        ></textarea>
      </div>

      <div class="actions">
        <button class="button button-secondary" @click=${this.handleBack}>
          ← back
        </button>
        <button
          class="button button-primary"
          @click=${this.handleSubmit}
          ?disabled=${this.submitting}
        >
          ${this.submitting ? 'saving...' : 'submit'}
        </button>
      </div>
    `;
  }
}
